import React, { useEffect, useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { getAuthHeaders } from '../../utils/auth';

export interface SponsorCardData {
   id: string;
   title: string;
   description?: string;
   imageUrl?: string;
   logoUrl?: string;
   sponsorName?: string;
   ctaText: string;
   ctaUrl: string;
   targeting?: { eventIds?: string[]; albumIds?: string[] };
   placement?: { position?: string; showInHotTab?: boolean };
}

interface SponsorCardProps {
   sponsor: SponsorCardData;
   userId: string;
}

const SponsorCard: React.FC<SponsorCardProps> = ({ sponsor, userId }) => {
   const impressionSent = useRef(false);

   const env = process.env.REACT_APP_FIREBASE_ENV || process.env.NODE_ENV || 'staging';

   useEffect(() => {
      if (impressionSent.current) return;
      impressionSent.current = true;

      getAuthHeaders().then((headers) => {
         fetch(`${process.env.REACT_APP_API_URL}/api/sponsors/impression?env=${env}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ sponsorCardId: sponsor.id }),
         }).catch(() => {});
      });
   }, [sponsor.id, env]);

   const handleCtaClick = async () => {
      try {
         const headers = await getAuthHeaders();
         await fetch(`${process.env.REACT_APP_API_URL}/api/sponsors/click?env=${env}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ sponsorCardId: sponsor.id, ctaUrl: sponsor.ctaUrl }),
         });
      } catch { /* best effort */ }
      window.open(sponsor.ctaUrl, '_blank');
   };

   return (
      <Box
         sx={{
            background: '#111815',
            border: '1px solid #1F2E2A',
            borderRadius: '12px',
            p: 2,
            position: 'relative',
         }}
      >
         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
               {sponsor.logoUrl && (
                  <Box
                     component="img"
                     src={sponsor.logoUrl}
                     alt=""
                     sx={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }}
                  />
               )}
               <Box
                  sx={{
                     width: 6,
                     height: 6,
                     borderRadius: '50%',
                     background: '#C9A84C',
                     flexShrink: 0,
                  }}
               />
            </Box>
            <Box
               sx={{
                  background: 'rgba(201,168,76,0.08)',
                  color: 'rgba(201,168,76,0.7)',
                  fontSize: '8px',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  px: 0.75,
                  py: 0.2,
                  borderRadius: '10px',
                  textTransform: 'uppercase',
               }}
            >
               Sponsored
            </Box>
         </Box>

         {sponsor.imageUrl && (
            <Box sx={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', mb: 1.5 }}>
               <Box
                  component="img"
                  src={sponsor.imageUrl}
                  alt={sponsor.title}
                  sx={{
                     width: '100%',
                     height: 140,
                     objectFit: 'cover',
                     display: 'block',
                  }}
               />
               <Box
                  sx={{
                     position: 'absolute',
                     bottom: 0,
                     left: 0,
                     right: 0,
                     height: 40,
                     background: 'linear-gradient(transparent, rgba(17,24,21,0.8))',
                  }}
               />
            </Box>
         )}

         <Typography sx={{ color: '#FFFFFF', fontSize: '15px', fontWeight: 600, mb: 0.5 }}>
            {sponsor.title}
         </Typography>
         {sponsor.description && (
            <Typography
               sx={{
                  color: '#90A4AE',
                  fontSize: '13px',
                  mb: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
               }}
            >
               {sponsor.description}
            </Typography>
         )}

         <Button
            onClick={handleCtaClick}
            fullWidth
            sx={{
               background: 'transparent',
               border: '1px solid #C9A84C',
               color: '#C9A84C',
               borderRadius: '8px',
               py: 1,
               fontWeight: 600,
               fontSize: '13px',
               textTransform: 'none',
               '&:hover': { background: 'rgba(201,168,76,0.1)' },
            }}
         >
            {sponsor.ctaText || 'Learn More'}
         </Button>

         {sponsor.sponsorName && (
            <Typography sx={{ color: '#546E7A', fontSize: '10px', mt: 1, textAlign: 'right' }}>
               Ad by {sponsor.sponsorName}
            </Typography>
         )}
      </Box>
   );
};

export default SponsorCard;
